import { Box } from "@mui/material";
import {
  CheckCircleOutlineOutlined,
  CurrencyRupeeOutlined,
  ErrorOutlineOutlined,
  HelpOutlineOutlined,
  ReceiptLongOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import InvoiceTable from "../../VendorsTabs/InvoiceProcess/InvoiceTable";
import styles from "../../VendorsTabs/InvoiceProcess/InvoiceProcess.module.css";

const formatCurrencyInr = (value) => {
  const n = Number(value ?? 0);
  if (!Number.isFinite(n)) return "-";
  return `₹ ${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const InvoiceReconciliation = () => {
  const loginData = useSelector((state) => state.loginData.value);

  const approverToken = loginData?.user?.token;
  const approverDesignation = loginData?.user?.designation;
  const authToken =
    loginData?.auth_token || loginData?.access_token || loginData?.token || "";

  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  const handleSummaryChange = useCallback((next) => {
    setSummary(next || null);
    setSummaryLoading(false);
  }, []);

  const summaryCards = useMemo(() => {
    const s = summary || {};
    const toInt = (v) => {
      const n = Number(v ?? 0);
      return Number.isFinite(n) ? n : 0;
    };

    const total = Math.max(1, toInt(s.total));
    const pct = (count) => Math.round((toInt(count) / total) * 100);

    return [
      {
        key: "total_invoices",
        label: "Total Invoices",
        value: toInt(s.total).toLocaleString("en-IN"),
        badge: "Queue",
        sub: "All invoices in approval queue",
        color: "blue",
        icon: <ReceiptLongOutlined className={styles.kpiIcon} />,
      },
      {
        key: "total_amount",
        label: "Total Amount (INR)",
        value: formatCurrencyInr(s.totalAmount),
        badge: "INR",
        sub: "Sum of invoice totals",
        color: "purple",
        icon: <CurrencyRupeeOutlined className={styles.kpiIcon} />,
      },
      {
        key: "matched",
        label: "Matched",
        value: toInt(s.matched).toLocaleString("en-IN"),
        badge: `${pct(s.matched)}%`,
        sub: "Fully matched",
        color: "green",
        icon: <CheckCircleOutlineOutlined className={styles.kpiIcon} />,
      },
      {
        key: "partial",
        label: "Partially Matched",
        value: toInt(s.partial).toLocaleString("en-IN"),
        badge: `${pct(s.partial)}%`,
        sub: "Needs review",
        color: "orange",
        icon: <WarningAmberOutlined className={styles.kpiIcon} />,
      },
      {
        key: "mismatch",
        label: "Exception Found",
        value: toInt(s.mismatched).toLocaleString("en-IN"),
        badge: `${pct(s.mismatched)}%`,
        sub: "Exception Found ",
        color: "red",
        icon: <ErrorOutlineOutlined className={styles.kpiIcon} />,
      },
      {
        key: "unverified",
        label: "Unverified",
        value: toInt(s.unverified).toLocaleString("en-IN"),
        badge: `${pct(s.unverified)}%`,
        sub: "Not compared yet",
        color: "gray",
        icon: <HelpOutlineOutlined className={styles.kpiIcon} />,
      },
    ];
  }, [summary]);

  return (
    <Box className={styles.container}>
      <Box className={styles.header}>
        <h1 className={styles.pageTitle}>Invoice Reconciliation</h1>
      </Box>

      <Box className={styles.kpiRow}>
        {summaryCards.map((card) => (
          <Box
            key={card.key}
            className={`${styles.kpiCard} ${styles[`kpiCard_${card.color}`]}`}
          >
            <Box className={styles.kpiCardHeader}>
              <Box
                className={`${styles.kpiIconWrap} ${styles[`kpiIconWrap_${card.color}`]}`}
              >
                {card.icon}
              </Box>
            </Box>
            <Box className={styles.kpiValueRow}>
              {summaryLoading ? (
                <span className={styles.kpiSkeletonValue} />
              ) : (
                <span className={styles.kpiValue}>{card.value}</span>
              )}
              {summaryLoading ? (
                <span className={styles.kpiSkeletonBadge} />
              ) : (
                <span
                  className={`${styles.kpiBadge} ${styles[`kpiBadge_${card.color}`]}`}
                >
                  {card.badge}
                </span>
              )}
            </Box>
            <Box className={styles.kpiLabelRow}>
              <span className={styles.kpiLabel}>{card.label}</span>
            </Box>
            <Box className={styles.kpiSubRow}>
              {summaryLoading ? (
                <span className={styles.kpiSkeletonSub} />
              ) : (
                <span className={styles.kpiSub}>{card.sub}</span>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      <InvoiceTable
        data={[]}
        mode="approver"
        AuthToken={authToken}
        ApproverToken={approverToken}
        ApproverDesignation={approverDesignation}
        queueStatus="all"
        onSummaryChange={handleSummaryChange}
      />
    </Box>
  );
};

export default InvoiceReconciliation;
